import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, Sparkles, Loader2, X, FileCheck2 } from "lucide-react";
import { Button } from "./ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function UploadCard({ onResult, requireAuth = false }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const onDrop = (accepted) => {
    const f = accepted[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
    disabled: loading,
  });

  const reset = () => {
    setFile(null);
    setPreview(null);
  };

  const runDemo = async () => {
    setLoading(true);
    try {
      // pretend inference time for demo
      await new Promise((r) => setTimeout(r, 1200));
      const { data } = await api.get("/demo/sample");
      onResult({
        ...data,
        image_name: file?.name || "demo.jpg",
        image_preview: preview,
        created_at: new Date().toISOString(),
      });
      toast.success("Demo analysis complete");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runReal = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/predict", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onResult({ ...data, image_preview: preview });
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Prediction failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = () => {
    if (!file) {
      toast.error("Please upload an image first");
      return;
    }
    if (requireAuth) runReal();
    else runDemo();
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6" data-testid="upload-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-400">
            {requireAuth ? "New scan" : "Demo mode"}
          </div>
          <div className="font-heading text-xl text-slate-900 mt-1">Skin image analysis</div>
        </div>
        {file && (
          <Button variant="ghost" size="sm" onClick={reset} className="rounded-full" data-testid="upload-reset-btn">
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      <div
        {...getRootProps()}
        className={`mt-5 relative rounded-2xl border-2 border-dashed transition-all cursor-pointer overflow-hidden ${
          isDragActive
            ? "border-sky-400 bg-sky-50"
            : "border-slate-200 hover:border-slate-300 bg-slate-50/60"
        } ${loading ? "pointer-events-none opacity-70" : ""}`}
        data-testid="upload-dropzone"
      >
        <input {...getInputProps()} data-testid="upload-input" />
        <div className="min-h-[220px] flex flex-col items-center justify-center p-8 text-center">
          {preview ? (
            <div className="w-full flex flex-col items-center gap-3">
              <img src={preview} alt="preview" className="max-h-52 rounded-xl ring-1 ring-slate-200 object-contain" />
              <div className="flex items-center gap-2 text-xs text-slate-500 font-mono-num">
                <FileCheck2 className="h-4 w-4 text-emerald-500" />
                {file?.name} · {(file?.size / 1024).toFixed(1)} KB
              </div>
            </div>
          ) : (
            <>
              <div className="h-12 w-12 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center">
                <UploadCloud className="h-5 w-5 text-slate-600" />
              </div>
              <div className="mt-4 font-heading text-lg text-slate-900">
                {isDragActive ? "Drop the image…" : "Drag & drop a skin image"}
              </div>
              <div className="text-sm text-slate-500 mt-1">or click to browse · JPG, PNG up to 8MB</div>
            </>
          )}
        </div>
      </div>

      <Button
        onClick={handleAnalyze}
        disabled={loading || !file}
        className="mt-5 w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
        data-testid="analyze-btn"
      >
        <AnimatePresence mode="wait" initial={false}>
          {loading ? (
            <motion.span key="l" className="flex items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Agents analysing…
            </motion.span>
          ) : (
            <motion.span key="i" className="flex items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Sparkles className="h-4 w-4 mr-2" /> Analyse image
            </motion.span>
          )}
        </AnimatePresence>
      </Button>

      {!requireAuth && (
        <div className="mt-3 text-[11px] text-center text-slate-500">
          Running in demo mode · <span className="text-slate-700 font-medium">Sign in</span> to save results.
        </div>
      )}
    </div>
  );
}
